import { ReactElement, useMemo } from 'react';
import {
  CheckboxGroupInput,
  NumberInput,
  SelectInput,
  TextInput,
  useGetManyReference,
} from 'react-admin';
import { LabelResource } from '../../../Entities/Label';
import { LabelValueResource } from '../../../Entities/LabelValue';
import { LABEL_INPUT_TYPE } from '../../Labels/inputTypes';
import { normalizeChoiceValue, type LabelChoice, toLabelChoices } from './utils';

interface LabelFieldProps {
  label: LabelResource;
  source: string;
}

export default function LabelField({ label, source }: LabelFieldProps): ReactElement {
  const { data: labelValues = [] } = useGetManyReference<LabelValueResource>(
    'label_values',
    {
      id: label.id,
      target: 'label.id',
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'attributes.display_order', order: 'ASC' },
    },
    {
      enabled: Boolean(label.id),
    },
  );

  const choices = useMemo<LabelChoice[]>(() => toLabelChoices(labelValues), [labelValues]);

  if (label.attributes.input_type === LABEL_INPUT_TYPE.LIST) {
    return (
      <CheckboxGroupInput
        choices={choices}
        format={(value) =>
          Array.isArray(value)
            ? value
                .filter((item): item is string => typeof item === 'string')
                .map((item) => normalizeChoiceValue(choices, item))
                .filter((item) => item.length > 0)
            : []
        }
        helperText={false}
        label={false}
        optionText="name"
        optionValue="id"
        row={false}
        source={source}
        translateChoice={false}
      />
    );
  }

  if (choices.length > 0) {
    return (
      <SelectInput
        choices={choices}
        format={(value) => (typeof value === 'string' ? normalizeChoiceValue(choices, value) : '')}
        emptyValue=""
        fullWidth
        helperText={false}
        label={false}
        optionText="name"
        optionValue="id"
        source={source}
        translateChoice={false}
      />
    );
  }

  if (label.attributes.input_type === LABEL_INPUT_TYPE.NUMBER) {
    return <NumberInput fullWidth helperText={false} label={false} source={source} />;
  }

  return <TextInput fullWidth helperText={false} label={false} source={source} />;
}
